// Single store: Orient Livsmedel. Products organised into Swedish sections.
// Website seed = /orient_products.json. Once imported into the shared backend
// (via admin "Import" tab), each section becomes a custom store named
// `Orient Livsmedel – <Swedish section>` so the admin can edit + upload images.

export const ORIENT_STORE_ID = "orient";
export const ORIENT_STORE_NAME = "Orient Livsmedel";

export const ORIENT_SECTIONS = [
  { slug: "erbjudanden", sv: "Erbjudanden", ar: "العروضات", emoji: "🏷️", color: "#E11D48" },
  { slug: "drycker", sv: "Drycker", ar: "مشروبات", emoji: "🥤", color: "#0EA5E9" },
  { slug: "kott-fryst", sv: "Kött & Fryst", ar: "لحوم ومجمّدات", emoji: "🥩", color: "#DC2626" },
  { slug: "mejeri-ost", sv: "Mejeri & Ost", ar: "ألبان وأجبان", emoji: "🧀", color: "#F59E0B" },
  { slug: "brod", sv: "Bröd", ar: "خبز", emoji: "🍞", color: "#B45309" },
  { slug: "frukt", sv: "Frukt", ar: "فواكه", emoji: "🍎", color: "#EF4444" },
  { slug: "gronsaker", sv: "Grönsaker", ar: "خضار", emoji: "🥬", color: "#16A34A" },
  { slug: "ris-bulgur", sv: "Ris & Bulgur", ar: "أرز وبرغل", emoji: "🍚", color: "#CA8A04" },
  { slug: "kryddor", sv: "Kryddor", ar: "بهارات", emoji: "🧂", color: "#EA580C" },
  { slug: "kaffe-te", sv: "Kaffe & Te", ar: "قهوة وشاي", emoji: "☕", color: "#78350F" },
  { slug: "notter-fron", sv: "Nötter & Frön", ar: "مكسرات وبذور", emoji: "🥜", color: "#A16207" },
  { slug: "snacks-godis", sv: "Snacks & Godis", ar: "سناكس وحلويات", emoji: "🍫", color: "#DB2777" },
  { slug: "konserver-inlagt", sv: "Konserver & Inlagt", ar: "معلّبات ومخلّلات", emoji: "🥫", color: "#0D9488" },
  { slug: "bonor-konserver", sv: "Bönor & Konserver", ar: "بقوليات ومعلّبات", emoji: "🫘", color: "#65A30D" },
  { slug: "hushall-rengoring", sv: "Hushåll & Rengöring", ar: "منظفات ومنزلية", emoji: "🧴", color: "#2563EB" },
  { slug: "ovrigt", sv: "Övrigt", ar: "أخرى", emoji: "🛒", color: "#6B7280" },
];

export const sectionStoreName = (sv) => `${ORIENT_STORE_NAME} – ${sv}`;
export const sectionBySlug = (slug) => ORIENT_SECTIONS.find((s) => s.slug === slug);
export const splitName = (name) => {
  const clean = parseProductMeta(name).clean;
  const [sv, ar] = String(clean || "").split(" | ");
  return { sv: (sv || "").trim(), ar: (ar || "").trim() };
};

// Product stock + status are encoded into the backend-persisted product name
// as a hidden suffix `  ⟦q=20;s=active⟧` so they sync to every customer.
export const PRODUCT_STATUSES = ["active", "inactive", "draft"];
const META_RE = /\s*⟦([^⟧]*)⟧\s*$/;
export function parseProductMeta(name) {
  const raw = String(name || "");
  const m = raw.match(META_RE);
  let stock = null;
  let status = "active";
  if (m) {
    m[1].split(";").forEach((kv) => {
      const [k, v] = kv.split("=");
      if (k === "q" && v != null && v !== "") { const n = Number(v); if (Number.isFinite(n)) stock = n; }
      if (k === "s" && v) status = v;
    });
  }
  return { clean: raw.replace(META_RE, "").trim(), stock, status };
}
export function encodeProductName(cleanName, { stock, status } = {}) {
  const base = String(cleanName || "").replace(META_RE, "").trim();
  const parts = [];
  if (stock !== null && stock !== undefined && stock !== "") parts.push(`q=${Number(stock)}`);
  if (status && status !== "active") parts.push(`s=${status}`);
  return parts.length ? `${base} ⟦${parts.join(";")}⟧` : base;
}

// Prefer real backend fields (p.stock / p.status); fall back to legacy name-encoded meta.
export function productMeta(p) {
  const base = parseProductMeta(p?.name);
  const hasReal = p && (p.status != null || p.stock != null);
  return {
    clean: base.clean,
    stock: hasReal ? (p.stock ?? null) : base.stock,
    status: hasReal ? (p.status || "active") : base.status,
  };
}

export const DELIVERY_FEE = 79;
export const FREE_DELIVERY_OVER = 399; // free delivery for orders above this amount
export const deliveryFeeFor = () => DELIVERY_FEE;

// Delivery config is stored in the shared backend as a custom store named
// `__DELIVERY__|<fee>|<freeUntilISO>|<freeOver>` so the admin controls it and all customers see it.
export const DELIVERY_CFG_PREFIX = "__DELIVERY__";
export const DEFAULT_FREE_UNTIL = "2026-10-14"; // first week free by default (admin editable)
export const DEFAULT_FREE_OVER = 399;
export function parseDeliveryConfig(storesData) {
  const list = storesData?.custom || [];
  const s = list.find((x) => String(x.name || "").startsWith(DELIVERY_CFG_PREFIX));
  if (s) {
    const parts = String(s.name).split("|");
    const fee = Number(parts[1]);
    const over = Number(parts[3]);
    return {
      fee: Number.isFinite(fee) ? fee : DELIVERY_FEE,
      freeUntil: parts[2] || null,
      freeOver: Number.isFinite(over) && over > 0 ? over : DEFAULT_FREE_OVER,
      storeId: s.store_id,
    };
  }
  return { fee: DELIVERY_FEE, freeUntil: DEFAULT_FREE_UNTIL, freeOver: DEFAULT_FREE_OVER, storeId: null };
}

// Legacy exports kept for the admin Stores/Products tabs.
export const CATEGORIES = [
  { id: "matbutik", sv: "Livsmedel", ar: "سوبرماركت", icon: "ShoppingCart" },
  { id: "restaurang", sv: "Restauranger", ar: "مطاعم", icon: "Utensils" },
  { id: "apotek", sv: "Apotek", ar: "صيدلية", icon: "Pill" },
  { id: "elektronik", sv: "Elektronik", ar: "إلكترونيات", icon: "Smartphone" },
  { id: "tobak", sv: "Tobak", ar: "تبغ", icon: "Cigarette", ageRestricted: true },
];
export const AGE_CATEGORIES = ["tobak"];
export const DEFAULT_STORES = [];

export function buildStores({ storesData, productsData }) {
  const custom = (storesData?.custom || []).filter((s) => !s.hidden).map((s) => ({
    store_id: s.store_id, name: s.name, category: s.category || "matbutik",
    color: s.color || "#0D9488", emoji: "🏬", isCustom: true,
  }));
  const products = (productsData?.products || []).filter((p) => !p.deleted);
  const byStore = {};
  products.forEach((p) => { (byStore[p.store_id] = byStore[p.store_id] || []).push(p); });
  return custom.map((s) => ({ ...s, products: byStore[s.store_id] || [], productCount: (byStore[s.store_id] || []).length }));
}
